import { CresoTextLogo } from "@core/components/logo/creso-logo";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { CUSTOM_AMC_LABELS_BY_AMC_ID } from "creso-core/constants";
import {
  clientDetailsQueryOptions,
  cobDetailedQueryOptions,
  fundFiltersQueryOptions,
} from "creso-core/hooks";
import { ChangeOfBrokerDetailed } from "creso-core/types";
import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "web-design-system";

export const Route = createFileRoute(
  "/(auth)/(headless)/reports/clients/$id/cob"
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const [client, cob, amc] = await Promise.all([
      queryClient.fetchQuery(clientDetailsQueryOptions(Number(params.id))),
      queryClient.fetchQuery(cobDetailedQueryOptions(Number(params.id))),
      queryClient.fetchQuery(fundFiltersQueryOptions("provider_company_name")),
    ]);
    return { client, cob, amc };
  },
});

function chunkAMCsByFolios(
  data?: ChangeOfBrokerDetailed
): ChangeOfBrokerDetailed {
  if (!data) return {} as ChangeOfBrokerDetailed;
  return {
    ...data,
    change_of_broker_details: data.change_of_broker_details.map((broker) => ({
      ...broker,
      amc: broker.amc.flatMap((amc) => {
        // If folio count is 5 or less, return as is
        if (amc.folio.length <= 5) {
          return [amc];
        }

        // Split folios into chunks of 5
        const chunks = [];
        for (let i = 0; i < amc.folio.length; i += 5) {
          chunks.push({
            ...amc,
            folio: amc.folio.slice(i, i + 5),
          });
        }
        return chunks;
      }),
    })),
  } as ChangeOfBrokerDetailed;
}

function RouteComponent() {
  const route = getRouteApi("/(auth)/(headless)/reports/clients/$id/cob");

  const { cob: cobData, amc: amcData } = route.useLoaderData();

  const data = useMemo(() => chunkAMCsByFolios(cobData), [cobData]);
  const today = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const getAMCLabel = (msCode: string, defaultName: string) => {
    let label = CUSTOM_AMC_LABELS_BY_AMC_ID[msCode] || "";

    if (!label) {
      label = amcData?.[msCode as keyof typeof amcData] || "";
    }

    if (!label) {
      label = defaultName
        .replace("_MF", " Mutual Fund")
        .replace(/MUTUALFUND/g, " Mutual Fund");
    }

    return label;
  };

  return (
    <>
      <style>{`
        @media print {
          @page {
            size: A4 portrait !important;
            margin: 0;
          }
          html {
            font-size: 14px;
          }
        }
      `}</style>
      {/* AMC Name and Date */}
      {data?.change_of_broker_details?.map((broker, brokerIdx) => (
        <>
          {broker.amc.map((amc, amcIdx) => (
            <div
              key={broker.broker_code + amc.amc_code}
              className="px-8 w-full max-w-5xl py-8 mx-auto break-before-page break-inside-avoid"
            >
              {/* Header */}
              <div className="flex justify-end mb-4">
                <CresoTextLogo />
              </div>

              {/* Main Title */}
              <div className="border border-border p-4 mb-4">
                <h3 className="text-center text-base! font-semibold mb-4">
                  Request for Change in Mutual Fund Distributor (MFD)
                </h3>
                <div key={brokerIdx} className="mb-8">
                  <div key={amcIdx} className="mb-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-semibold text-sm">
                        {getAMCLabel(amc.morning_star_code, amc.amc_name)}
                      </span>
                      <span className="font-semibold text-sm">
                        Date: {today}
                      </span>
                    </div>

                    {/* Folio and Scheme Table */}
                    <Table className="border border-border rounded-lg mb-4">
                      <TableHeader className="bg-muted rounded-t-lg">
                        <TableRow>
                          <TableHead className="p-2 text-xs border-r border-border font-semibold">
                            Folio No <span className="italic">(Mandatory)</span>
                          </TableHead>
                          <TableHead className="p-2 text-xs font-semibold">
                            Scheme Name{" "}
                            <span className="italic">
                              (Required if change request is for specific
                              schemes)
                            </span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {amc.folio.map((folioItem, folioIdx) => (
                          <TableRow
                            key={folioIdx}
                            className="border-b border-border"
                          >
                            <TableCell className="p-2 text-xs border-r border-border">
                              {folioItem.folio_number}
                            </TableCell>
                            <TableCell className="p-2 text-xs">
                              All Schemes
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Empty rows for form structure */}
                        {[...Array(Math.max(0, 5 - amc.folio.length))].map(
                          (_, idx) => (
                            <TableRow
                              key={`empty-${idx}`}
                              className="border-b border-border h-12"
                            >
                              <TableCell className="p-2 text-xs border-r border-border">
                                &nbsp;
                              </TableCell>
                              <TableCell className="p-2 text-xs">
                                &nbsp;
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>

                    {/* Old and New ARN Details */}
                    <Table className="border border-border rounded-lg mb-4">
                      <TableHeader className="bg-muted rounded-t-lg">
                        <TableRow>
                          <TableHead className="p-2 text-xs border-r border-border font-semibold">
                            Old ARN Code
                          </TableHead>
                          <TableHead className="p-2 text-xs border-r border-border font-semibold">
                            Old ARN Name
                          </TableHead>
                          <TableHead className="p-2 text-xs border-r border-border font-semibold">
                            New ARN Code
                          </TableHead>
                          <TableHead className="p-2 text-xs border-r border-border font-semibold">
                            New ARN Name
                          </TableHead>
                          <TableHead className="p-2 text-xs border-r border-border font-semibold">
                            New Sub-ARN Code
                          </TableHead>
                          <TableHead className="p-2 text-xs font-semibold">
                            New EUIN Code
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="p-2 text-xs border-r border-border">
                            {broker.broker_code}
                          </TableCell>
                          <TableCell className="p-2 text-xs border-r border-border">
                            {broker.broker_name}
                          </TableCell>
                          <TableCell className="p-2 text-xs border-r border-border">
                            ARN-{data.new_broker_details.arn_code}
                          </TableCell>
                          <TableCell className="p-2 text-xs border-r border-border">
                            {data.new_broker_details.name}
                          </TableCell>
                          <TableCell className="p-2 text-xs border-r border-border">
                            ARN-{data.new_broker_details.sub_broker_code}
                          </TableCell>
                          <TableCell className="p-2 text-xs">
                            {data.new_broker_details.sub_broker_euin}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {/* Declaration Note */}
                    <div className="text-xxs italic mb-4">
                      All fields are mandatory, except New Sub-ARN Code, which
                      may be filled in, only if applicable
                    </div>

                    {/* Declaration by Investor */}
                    <div className="mb-4">
                      <div className="font-semibold mb-2">
                        Declaration by Investor
                      </div>
                      <div className="text-xxs mb-4">
                        I/We are having investments with{" "}
                        {getAMCLabel(amc.morning_star_code, amc.amc_name)} vide
                        folio/s mentioned above, want to change the MFD ARN code
                        in my folio/s as per the details provided. I confirm
                        that I am not misguided or lured to change the ARN code
                        and submitting this request with full knowledge and
                        understanding of the changes, voluntarily. I also
                        understand and agree that the change request once
                        processed, can&apos;t be revoked and a fresh request
                        needs to be raised for reversal of such changes.
                      </div>

                      {/* Investor Details Table */}
                      <Table className="border border-border mb-4">
                        <TableHeader className="bg-muted rounded-t-lg">
                          <TableRow>
                            <TableHead className="p-2 text-xs border-r border-border font-semibold">
                              Investor Details
                            </TableHead>
                            <TableHead className="p-2 text-xs border-r border-border font-semibold">
                              1st holder
                            </TableHead>
                            <TableHead className="p-2 text-xs border-r border-border font-semibold">
                              2nd holder
                            </TableHead>
                            <TableHead className="p-2 text-xs font-semibold">
                              3rd holder
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="p-2 text-xs border-r border-border font-semibold">
                              Name
                            </TableCell>
                            <TableCell className="p-2 text-xs border-r border-border">
                              {data.investor_details.name}
                            </TableCell>
                            <TableCell className="p-2 text-xs border-r border-border">
                              &nbsp;
                            </TableCell>
                            <TableCell className="p-2 text-xs">
                              &nbsp;
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="p-2 text-xs border-r border-border font-semibold">
                              Signature
                              <div className="text-xxs font-normal">
                                (To be signed as per Mode of holding)
                              </div>
                            </TableCell>
                            <TableCell className="p-2 text-xs border-r border-border">
                              &nbsp;
                            </TableCell>
                            <TableCell className="p-2 text-xs border-r border-border">
                              &nbsp;
                            </TableCell>
                            <TableCell className="p-2 text-xs">
                              &nbsp;
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>

                      {/* Declaration of MFD */}
                      <div className="font-semibold mb-2">
                        Declaration of MFD (new ARN/EUIN holder)
                      </div>
                      <div className="text-xxs mb-4">
                        I hereby affirm that the aforementioned request for the
                        change of ARN in the specified
                        folio&apos;s/scheme&apos;s has been initiated with the
                        explicit and informed consent of the investor. The
                        investor has been fully apprised of the nature and
                        implications of this change request. Furthermore, no
                        force, coercion, or inducement of any kind was employed
                        to influence the investor&apos;s decision.
                      </div>

                      {/* MFD Details */}
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2 mt-auto text-xxs">
                        <div className="flex items-start gap-2">
                          <span>New ARN:</span>
                          <span className="font-semibold">
                            {data.new_broker_details.arn_code}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span>ARN Name:</span>
                          <span className="font-semibold">
                            {data.new_broker_details.name}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span>
                            Sub-Distributor&apos;s ARN: <br /> (if applicable)
                          </span>
                          <span className="font-semibold">
                            {data.new_broker_details.sub_broker_code}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span>
                            Sub-Distributor&apos;s Name: <br /> (if applicable)
                          </span>
                          <span className="font-semibold">
                            {data.new_broker_details.sub_broker_name}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span>EUIN NO.:</span>
                          <span className="font-semibold">
                            {data.new_broker_details.sub_broker_euin}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span>EUIN NO. Name:</span>
                          <span className="font-semibold">
                            {data.new_broker_details.sub_broker_name}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-8 col-span-2">
                          <div className="flex flex-col items-start col-span-1 gap-2">
                            <div className="flex items-start gap-2">
                              <span>Date:</span>
                              <span className="font-semibold">{today}</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span>Place:</span>
                              <span className="font-semibold">
                                {data.investor_details.place}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-end col-span-1 gap-2">
                            <span>
                              Signature of ARN/EUIN Holder:&nbsp;
                              ______________________________________
                              <br />
                              (Name, Designation, Employee code of new
                              distributor if non individual)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      ))}
    </>
  );
}
